import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { Box, useTheme } from '@mui/material'
import React from 'react'
import { useDispatch } from 'react-redux'
import { setActiveInstallationAndStorage } from 'src/Libs/installations/installation.functions'
import { makeStyles } from 'tss-react/mui'
import NoInstallationIdTooltip from './NoInstallationIdTooltip'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { useUserRoles } from 'src/Hooks/user'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { getInstallationDashboardUrl } from 'src/Libs/dashboard/routes'

type PartialInstallation = Pick<
  Installation,
  | 'id'
  | 'emsMasterDeviceId'
  | 'partnersInstallationName'
  | 'permissionType'
  | 'accessRequestAt'
  | 'ownerFirstName'
  | 'ownerLastName'
  | 'ownerAccountType'
  | 'ownerCompanyName'
  | 'installationStatus'
>

interface Props {
  installation: PartialInstallation
  linkClassName?: string
  displayStorageNumber?: boolean
  disabled?: boolean
}

const useStyles = makeStyles()(theme => ({
  installationLinkWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  linkButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
}))

/**
 * TODO: handle the previous and now disabled target link generation properly (line 50)
 */
const InstallationLink: React.FC<Props> = ({ installation, linkClassName, displayStorageNumber = false, disabled = false }) => {
  const theme = useTheme()
  const { classes } = useStyles()
  const dispatch = useDispatch()

  const { isServiceUser } = useUserRoles()
  const installationAccessType = useInstallationAccessType(installation as Installation)

  const isLinkDisabled = disabled || installationAccessType === 'access-denied'
  const shouldLinkToConfigurator = installationAccessType === 'e3dc-setup-configurator-access'
  const isPlannedInstallation =
    installationAccessType === 'e3dc-setup-configurator-access' || installationAccessType === 'e3dc-setup-restricted-access'
  const showInstallationName = !!installation.partnersInstallationName && (!isPlannedInstallation || isServiceUser)
  const shouldLinkToInstallationDetails = isServiceUser && !isPlannedInstallation

  const installationId = installation.id

  const getLinkTarget = (): string => {
    if (shouldLinkToInstallationDetails) {
      return `/overview/installations/${installationId}`
    }

    if (shouldLinkToConfigurator) {
      return `/installation/${installationId}/configuration/general`
    }

    return getInstallationDashboardUrl(installation)
  }

  if (!installationId) {
    return <NoInstallationIdTooltip />
  }

  return (
    <Box className={classes.installationLinkWrapper}>
      <Box className={classes.linkButtonWrapper}>
        <LinkButton
          linkClassName={linkClassName}
          content={displayStorageNumber ? installation.emsMasterDeviceId : installation.id}
          linkColor={theme.palette.primary.main}
          hasUnderline={false}
          link={getLinkTarget()}
          onClick={() => setActiveInstallationAndStorage(installation, dispatch)}
          clickable={!isLinkDisabled}
          dataQa={`button-installation-id-${installation.id}`}
        />
        {showInstallationName && <span>{installation.partnersInstallationName}</span>}
      </Box>
    </Box>
  )
}

export default InstallationLink
